import React, { useEffect, useState } from 'react';
import { colors } from '../styles/styles';
import ThoughtsList from '../components/ThoughtsList';

/**
 * MailView – displays up to 5 insight thoughts (importance 4-5) that refresh once per calendar day.
 *
 * Selection logic:
 * 1. On first visit each day we pick 5 random eligible thoughts and cache their IDs in localStorage.
 * 2. Subsequent visits on the same day read the cached IDs to ensure a stable set for the day.
 * 3. When the date changes a new selection is generated automatically.
 *
 * @param {Object}   props
 * @param {Array}    props.thoughts  – All thoughts available to the user.
 */
const MailView = ({ thoughts = [] }) => {
    const [dailyQuotes, setDailyQuotes] = useState([]);

    // Utility to get yyyy-mm-dd string for today
    const getTodayKey = () => new Date().toISOString().split('T')[0];

    useEffect(() => {
        if (!thoughts.length) return;

        const todayKey = getTodayKey();
        const storageKey = `mailQuotes_${todayKey}`;
        const cached = localStorage.getItem(storageKey);

        if (cached) {
            // Read cached IDs and map back to thought objects (filter in case some were deleted)
            try {
                const cachedIds = JSON.parse(cached);
                const cachedThoughts = thoughts.filter(t => cachedIds.includes(t.id));
                setDailyQuotes(cachedThoughts);
            } catch (err) {
                console.error('Failed to parse cached mail quotes – regenerating.', err);
                localStorage.removeItem(storageKey);
            }
        }

        // If no valid cache, generate a new set and store it
        if (!cached) {
            // Filter eligible thoughts (insight flag + importance >= 4)
            const eligible = thoughts.filter(t => !!t.insight && (t.importance || 0) >= 4);
            // Shuffle
            const shuffled = [...eligible].sort(() => Math.random() - 0.5);
            const selection = shuffled.slice(0, 5);
            setDailyQuotes(selection);
            localStorage.setItem(storageKey, JSON.stringify(selection.map(t => t.id)));
        }
    }, [thoughts]);

    // Render using the shared ThoughtsList component for consistency
    return (
        <div style={{ paddingTop: '80px' }}>
            {dailyQuotes.length === 0 ? (
                <p style={{ 
                    textAlign: 'center', 
                    marginTop: '120px', 
                    color: colors.textSecondary 
                }}>
                    No eligible insights yet. Mark some thoughts as insight and set importance to 4 or 5 to start receiving daily quotes.
                </p>
            ) : (
                <ThoughtsList 
                    thoughts={dailyQuotes}
                    title={`Daily Scroll – ${new Date().toLocaleDateString()}`}
                    defaultSort="chronological"
                />
            )}
        </div>
    );
};

export default MailView; 